import React, { useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

/**
 * Auto-playing video that starts muted (so it autoplays per browser policy)
 * and exposes a small floating "tap for sound" toggle so the user can unmute
 * on demand. Plays ONCE by default (no loop) — pass loop={true} to repeat.
 *
 * Props mirror the underlying <video> intent:
 *   src           — required, the .mp4 URL
 *   testId        — data-testid for the <video>
 *   aspectRatio   — CSS aspect-ratio string e.g. "9 / 16", "16 / 9" (default 16/9)
 *   maxWidth      — outer container max width in px (default 252)
 *   objectFit     — "cover" | "contain" (default "cover")
 *   loop          — repeat playback (default false: autoplay once, then stop)
 */
export default function VideoWithSoundToggle({
  src,
  testId = 'intro-video',
  aspectRatio = '16 / 9',
  maxWidth = 252,
  objectFit = 'cover',
  loop = false,
  containerStyle = {},
}) {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggle = (e) => {
    e.stopPropagation();
    const v = ref.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    setMuted(next);
    // Some browsers pause when changing muted programmatically — kick it.
    if (v.paused) v.play().catch(() => {});
  };

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxWidth,
        margin: '0 auto',
        borderRadius: 14,
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
        aspectRatio,
        backgroundColor: '#000',
        ...containerStyle,
      }}
    >
      <video
        ref={ref}
        data-testid={testId}
        src={src}
        autoPlay
        muted
        playsInline
        loop={loop}
        preload="auto"
        style={{ width: '100%', height: '100%', objectFit, display: 'block' }}
      />

      <button
        type="button"
        onClick={toggle}
        data-testid={`${testId}-sound-toggle`}
        aria-label={muted ? 'Unmute video' : 'Mute video'}
        style={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          width: 32,
          height: 32,
          borderRadius: 9999,
          border: 'none',
          backgroundColor: 'rgba(0,0,0,0.6)',
          color: '#FFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          backdropFilter: 'blur(4px)',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.8)')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.6)')}
      >
        {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
      </button>

      {muted && (
        <div
          onClick={toggle}
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            padding: '4px 10px',
            borderRadius: 9999,
            backgroundColor: 'rgba(0,0,0,0.6)',
            color: '#FFF',
            fontSize: 11,
            fontWeight: 600,
            cursor: 'pointer',
            backdropFilter: 'blur(4px)',
            userSelect: 'none',
          }}
        >
          Tap for sound
        </div>
      )}
    </div>
  );
}
